
import { useEffect, useState } from "react";
import { getEnvironment, BUILD_MARKER } from "@/utils/previewCacheGuard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, ShieldCheck, AlertTriangle, Info, Smartphone } from "lucide-react";
import { getServiceWorkerStatus, getSwPhase } from "@/pwa/registerServiceWorker";
import { isStandalone } from "@/pwa/pwaEnvironment";
import { APP_TARGETS, PWA_APP, resolveModuleFromPath } from "@/pwa/appTargets";
import { getPendingUpdate, getAppliedVersion, getCurrentBuild, getRemoteBuild, getLastUpdateCheck, getSnoozedUntil, getSwError, getRecovery, getUpdateAttempt } from "@/pwa/updateState";
import { getAppBusyState } from "@/pwa/busyRegistry";
import ViewportDiagnosticsCard from "@/components/diagnostics/ViewportDiagnosticsCard";


const Diagnostics = () => {
  const [swStatus, setSwStatus] = useState<string>("Verificando...");
  const [cacheStatus, setCacheStatus] = useState<string>("Verificando...");
  const [pwaStatus, setPwaStatus] = useState({
    waiting: false,
    installing: false,
    active: false,
    standalone: false,
    notifications: 'Verificando...' as string,
    scope: null as string | null,
    scriptUrl: null as string | null,
  });
  const [, refreshUpdateStatus] = useState(0);
  useEffect(() => {
    const refresh = () => refreshUpdateStatus(value => value + 1);
    window.addEventListener("autofluxo:update-status", refresh);
    window.addEventListener("storage", refresh);
    const timer = window.setInterval(refresh, 1000);
    return () => { window.clearInterval(timer); window.removeEventListener("autofluxo:update-status", refresh); window.removeEventListener("storage", refresh); };
  }, []);
  const [loadTime] = useState(new Date().toLocaleString());
  const env = getEnvironment();
  const isPreview = env === 'preview' || env === 'development';
  const moduleId = resolveModuleFromPath();
  const busyState = getAppBusyState();
  const pendingVersion = getPendingUpdate();
  const appliedVersion = getAppliedVersion();

  useEffect(() => {
    void (async () => {
      const sw = await getServiceWorkerStatus();
      const notifications = typeof Notification === 'undefined'
        ? 'Não suportado neste navegador'
        : Notification.permission === 'granted'
          ? 'Permitidas'
          : Notification.permission === 'denied'
            ? 'Bloqueadas'
            : 'Ainda não solicitadas';
      setPwaStatus({
        waiting: sw.waiting,
        installing: sw.installing,
        active: sw.active,
        standalone: isStandalone(),
        notifications,
        scope: sw.scope,
        scriptUrl: sw.scriptUrl,
      });
    })();
  }, []);



  useEffect(() => {
    const checkSW = async () => {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        setSwStatus(regs.length > 0 ? `${regs.length} registrado(s)` : "Nenhum Service Worker");
      } else {
        setSwStatus("Não suportado");
      }
    };

    const checkCache = async () => {
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys();
        setCacheStatus(keys.length > 0 ? `${keys.length} caches ativos` : "Caches limpos");
      } else {
        setCacheStatus("Não suportado");
      }
    };

    checkSW();
    checkCache();
  }, []);

  const forceReload = () => {
    window.location.reload();
  };

  const clearAndReload = async () => {
    if (confirm("Isso irá deslogar você se não houver um cookie de sessão persistente. Continuar?")) {
      localStorage.clear();
      sessionStorage.clear();
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
      }
      window.location.href = "/login";
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-4 md:p-8 font-sans">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Diagnóstico do App</h1>
            <p className="text-slate-500">Informações técnicas sobre o build e ambiente.</p>
          </div>
          <Badge variant={isPreview ? "secondary" : "default"} className="px-3 py-1">
            {env.toUpperCase()}
          </Badge>
        </header>

        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-green-500" />
                Build Info
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Build Marker</label>
                <div className="p-2 bg-slate-100 rounded text-sm font-mono break-all border border-slate-200">
                  {BUILD_MARKER}
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Timestamp de Carregamento</label>
                <div className="text-sm">{loadTime}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-indigo-500" />
                Aplicativo
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Identificado como</label>
                 <div className="text-sm font-medium">{PWA_APP.name}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Endereço atual</label>
                <div className="text-sm font-mono break-all">{window.location.origin}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Módulo visualizado</label>
                <div className="text-sm">{moduleId}</div>
              </div>
               <div>
                 <label className="text-xs font-semibold text-muted-foreground uppercase">Domínio principal</label>
                 <div className="text-sm">{PWA_APP.host}</div>
               </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Manifesto / ID</label>
                 <div className="text-sm font-mono break-all">{PWA_APP.manifest} · {PWA_APP.manifestId}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Service worker</label>
                <div className="text-sm font-mono break-all">{pwaStatus.scriptUrl || 'Nenhum'}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Escopo</label>
                <div className="text-sm font-mono break-all">{pwaStatus.scope || '—'}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Build carregado</label>
                <div className="text-sm font-mono break-all">{getCurrentBuild()}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Build remoto (version.json)</label>
                <div className="text-sm font-mono break-all">{getRemoteBuild() || 'Ainda não consultado'}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Estado do service worker</label>
                <div className="text-sm">{(() => {
                  const phase = getSwPhase();
                  const err = getSwError();
                  const state = phase === 'error' ? 'Erro'
                    : pwaStatus.installing || phase === 'installing' ? 'Instalando nova versão'
                    : pwaStatus.waiting || phase === 'waiting' ? 'Nova versão esperando'
                    : pwaStatus.active ? 'Ativo' : 'Nenhum';
                  return <>{state}{err && <span className="block text-xs text-muted-foreground">Última falha: {err.reason} — {new Date(err.at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} (Brasília)</span>}</>;
                })()}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Última tentativa / rede de segurança</label>
                <div className="text-sm">{(() => {
                  const a = getUpdateAttempt();
                  const r = getRecovery();
                  const f = (t: number) => new Date(t).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
                  return <>{a ? `Tentativa: ${f(a.at)}` : 'Nenhuma tentativa'}
                    {r && <span className="block text-xs text-muted-foreground">Atrasado desde {f(r.firstAt)} · {r.attempts} tentativa(s){r.cleanedAt ? ` · limpeza automática em ${f(r.cleanedAt)}` : ''}</span>}</>;
                })()}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Última checagem de atualização</label>
                <div className="text-sm">{getLastUpdateCheck() ? new Date(getLastUpdateCheck()).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' }) : 'Ainda não realizada'} (Brasília)</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Adiamento ativo</label>
                <div className="text-sm">{getSnoozedUntil() > Date.now() ? `Até ${new Date(getSnoozedUntil()).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })} (Brasília)` : 'Não'}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Versão aplicada / pendente</label>
                <div className="text-sm">{appliedVersion || '—'} / {pendingVersion || 'nenhuma'}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Trabalho em andamento</label>
                <div className="text-sm">
                  {busyState === 'busy'
                    ? 'Sim (atualização adiada)'
                    : busyState === 'unknown'
                      ? 'Indeterminado (aba sem resposta)'
                      : 'Não'}
                </div>
              </div>

            </CardContent>
          </Card>


          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <RefreshCw className="w-5 h-5 text-primary" />
                Status do Cache
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Service Workers</label>
                <div className="text-sm font-medium">{swStatus}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Cache Storage</label>
                <div className="text-sm font-medium">{cacheStatus}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Atualização pendente</label>
                <div className="text-sm font-medium">{pwaStatus.waiting ? 'Sim (nova versão pronta)' : 'Não'}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Modo instalado</label>
                <div className="text-sm font-medium">{pwaStatus.standalone ? 'Sim (standalone)' : 'Não (navegador)'}</div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase">Notificações</label>
                <div className="text-sm font-medium">{pwaStatus.notifications}</div>
              </div>
            </CardContent>
          </Card>

          <ViewportDiagnosticsCard />

        </div>

        <Card className="border-amber-200 bg-amber-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              Verificação de Preview
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-amber-900">
              Se você fez uma alteração e ela não aparece:
            </p>
            <ul className="text-sm list-disc pl-5 space-y-1 text-amber-800">
              <li>Confira se o <strong>Build Marker</strong> acima mudou após sua edição.</li>
              <li>O preview do Lovable às vezes trava em versões antigas devido ao cache do navegador ou proxies.</li>
              <li>Nesta página, os Service Workers em ambiente de preview são automaticamente removidos pelo <code>previewCacheGuard</code>.</li>
            </ul>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button onClick={forceReload} variant="outline" size="sm" className="bg-white">
                <RefreshCw className="w-4 h-4 mr-2" /> Atualizar Página
              </Button>
              <Button onClick={clearAndReload} variant="destructive" size="sm">
                Limpeza Total (Nuclear)
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Info className="w-5 h-5 text-slate-500" />
              Como validar?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 leading-relaxed">
              Abra o console do navegador (F12) e procure por uma linha começando com 
              <code className="mx-1 px-1 bg-muted rounded text-primary font-bold">[AutoFluxo]</code>. 
              Se o build ID no console bater com o que você vê nesta página, o bundle que você está rodando é o mais recente que chegou ao seu navegador.
            </p>
          </CardContent>
        </Card>

        <footer className="text-center pt-4">
          <Button variant="link" onClick={() => window.history.back()} className="text-muted-foreground">
            Voltar
          </Button>
        </footer>
      </div>
    </div>
  );
};

export default Diagnostics;
